import {
  Box,
  VStack,
  FormControl,
  FormLabel,
  Text,
  Checkbox,
  Textarea,
  Divider,
  Button,
  Select,
  Input,
} from '@chakra-ui/react';
import { useWatch } from 'react-hook-form';
import type { Control, UseFormSetValue } from 'react-hook-form';
import { useMemo } from 'react';
import { MdAdd } from 'react-icons/md';
import { TaskType, TaskFormData, ApprovalConfigUI, ApprovalLevelUI } from '@/lib/sdk/chatbot/types';
import { useTeamMembers } from '@/lib/sdk/team/queries';
import { useAuth } from '@/lib/sdk/auth/hooks';
import { ApprovalLevelCard } from './ApprovalLevelCard';

// Cap from docs/maker-checker-design.md Q2's default-if-unanswered: no confirmed customer need
// for more, and the WhatsApp button payload's level segment is [0-9]{1,2} regardless.
const MAX_LEVELS = 5;

// Session texts sent outside the approval template. Each falls back to the built-in
// backend default when left blank, so an untouched task keeps its current wording.
// The placeholder tokens mirror those accepted by the Go renderer in
// internal/workflow/messages/approval_text.go. These stay global (not per-level) —
// docs/maker-checker-design.md §7.1 keeps them global unless a customer needs per-level wording.
const MESSAGE_FIELDS: {
  key: 'makerPendingText' | 'makerApprovedText' | 'makerRejectedText' | 'approverApprovedText' | 'approverRejectedText';
  label: string;
  helper: string;
  placeholder: string;
}[] = [
  {
    key: 'makerPendingText',
    label: 'To maker — request sent',
    helper: 'Sent to the submitter as soon as their entry goes out for approval. {{approver}} lists every approver.',
    placeholder: '⏳ *{{task}}*\n\nYour request has been sent, you will be notified.',
  },
  {
    key: 'makerApprovedText',
    label: 'To maker — approved',
    helper: 'Sent to the submitter when the chain finishes approved.',
    placeholder: '✅ *{{task}}*\n\n{{description}}',
  },
  {
    key: 'makerRejectedText',
    label: 'To maker — rejected',
    helper: 'Sent to the submitter when the chain finishes rejected.',
    placeholder: '❌ *{{task}}*\n\nYour submission was not approved.',
  },
  {
    key: 'approverApprovedText',
    label: 'To approver — approved',
    helper: 'Confirmation sent back to an approver who approved.',
    placeholder: '✅ You approved *{{task}}* submitted by {{maker}}.',
  },
  {
    key: 'approverRejectedText',
    label: 'To approver — rejected',
    helper: 'Confirmation sent back to an approver who rejected.',
    placeholder: '❌ You rejected *{{task}}* submitted by {{maker}}.',
  },
];

const RESOLVERS_NEEDING_SOURCE_TASK = ['number_with_value', 'number_with_image'];

interface MakerCheckerConfigSectionProps {
  control: Control<TaskFormData>;
  setValue: UseFormSetValue<TaskFormData>;
  taskType: string;
  availableTasks: Array<{ id: string; name: string }>;
  // 'approval' (default) renders the full maker-checker approval config, with a repeater over
  // one or more approval levels.
  // 'alert' renders a trimmed one-way team-alert config: contacts + template only, and never
  // grows a levels array — one-way notifications have no quorum to configure
  // (docs/maker-checker-design.md §7.3).
  mode?: 'approval' | 'alert';
}

export const MakerCheckerConfigSection = ({
  control,
  setValue,
  taskType,
  availableTasks,
  mode = 'approval',
}: MakerCheckerConfigSectionProps) => {
  const isAlertMode = mode === 'alert';
  const { currentOrg } = useAuth();

  const { data: teamMembersData, isLoading: teamMembersLoading } = useTeamMembers({
    org_id: currentOrg?.id ?? '',
    offset: 0,
    limit: 1200,
  });

  const teamMemberOptions = useMemo(
    () =>
      teamMembersData?.items.map(m => ({
        value: m.country_code + m.phone_number,
        label: `${m.name} (${m.country_code + m.phone_number})`,
        name: m.name,
        phone: m.country_code + m.phone_number,
      })) || [],
    [teamMembersData]
  );

  const watchedApprovalConfig = (useWatch({ control, name: 'approvalConfig' }) ?? {}) as ApprovalConfigUI;

  // `levels` is authoritative when present. When absent — every task authored before this
  // editor shipped, and every alert-mode task always — synthesize a single level from the
  // flat fields so the author sees their existing config rather than a blank editor. Nothing
  // is written to form state by this synthesis; only handleLevelChange (below) actually calls
  // setValue, so a task nobody edits through this UI stays byte-identical to before this
  // feature (docs/maker-checker-design.md §7.2 — the read-time adapter is the whole point).
  const levels: ApprovalLevelUI[] = useMemo(() => {
    if (watchedApprovalConfig.levels && watchedApprovalConfig.levels.length > 0) {
      return [...watchedApprovalConfig.levels].sort((a, b) => a.level - b.level);
    }
    return [{
      level: 1,
      contactType: watchedApprovalConfig.contactType || 'manual',
      approvalContacts: watchedApprovalConfig.approvalContacts || [],
      approvalTemplate: watchedApprovalConfig.approvalTemplate,
      approvalResolver: watchedApprovalConfig.approvalResolver,
      templateBuilder: watchedApprovalConfig.templateBuilder,
      quorum: 'any',
    }];
  }, [
    watchedApprovalConfig.levels,
    watchedApprovalConfig.contactType,
    watchedApprovalConfig.approvalContacts,
    watchedApprovalConfig.approvalTemplate,
    watchedApprovalConfig.approvalResolver,
    watchedApprovalConfig.templateBuilder,
  ]);

  // Alert mode must never grow a levels array — it writes the one level straight back to the
  // flat fields, exactly as this component always has. Approval mode writes through `levels`.
  const handleLevelChange = (index: number, updated: ApprovalLevelUI) => {
    if (isAlertMode) {
      setValue('approvalConfig.contactType', updated.contactType);
      setValue('approvalConfig.approvalContacts', updated.approvalContacts);
      setValue('approvalConfig.approvalTemplate', updated.approvalTemplate);
      setValue('approvalConfig.approvalResolver', updated.approvalResolver);
      setValue('approvalConfig.templateBuilder', updated.templateBuilder);
      return;
    }
    const next = levels.map((level, i) => (i === index ? updated : level));
    setValue('approvalConfig.levels', next);
  };

  const handleAddLevel = () => {
    const nextOrdinal = (levels[levels.length - 1]?.level ?? 0) + 1;
    setValue('approvalConfig.levels', [
      ...levels,
      { level: nextOrdinal, contactType: 'manual', approvalContacts: [], quorum: 'any' },
    ]);
  };

  const handleRemoveLevel = (index: number) => {
    const remaining = levels
      .filter((_, i) => i !== index)
      .map((level, i) => ({ ...level, level: i + 1 }));
    setValue('approvalConfig.levels', remaining);
  };

  const isDocumentWithMakerChecker = taskType === TaskType.DOCUMENT;
  const needsSourceTask = !isAlertMode && levels.some(
    (level) => RESOLVERS_NEEDING_SOURCE_TASK.includes(level.approvalResolver || 'default')
  );

  return (
    <>
      <Divider />
      <Box>
        <VStack align="stretch" spacing={4}>
          <FormLabel
            color="text-primary"
            fontSize="md"
            fontWeight="medium"
            mb={0}
          >
            {isAlertMode ? 'Team Alert Configuration' : 'Approval Configuration'}
          </FormLabel>
          <Text fontSize="sm" color="text-secondary">
            {isAlertMode
              ? 'Configure who gets notified when this task fires. Contacts receive a one-way WhatsApp template — no approval is requested.'
              : 'Configure who should approve this task. Add another level for a chain — e.g. a store manager first, then finance — each with its own approvers and template.'}
          </Text>

          <VStack align="stretch" spacing={3}>
            {(isAlertMode ? [levels[0]] : levels).map((level, index) => (
              <ApprovalLevelCard
                key={level.level}
                level={level}
                index={index}
                totalLevels={levels.length}
                onChange={(updated) => handleLevelChange(index, updated)}
                onRemove={!isAlertMode && levels.length > 1 ? () => handleRemoveLevel(index) : undefined}
                taskType={taskType}
                teamMemberOptions={teamMemberOptions}
                teamMembersLoading={teamMembersLoading}
                availableTasks={availableTasks}
                variant={isAlertMode ? 'alert' : 'full'}
              />
            ))}
            {!isAlertMode && levels.length < MAX_LEVELS && (
              <Button
                leftIcon={<MdAdd />}
                size="sm"
                variant="outline"
                alignSelf="flex-start"
                onClick={handleAddLevel}
                data-testid="wfv4-approval-add-level"
              >
                Add level
              </Button>
            )}
          </VStack>

          {/* Source Task — shown when any level's resolver uses args.Text (number_with_value, number_with_image) */}
          {needsSourceTask && (
            <FormControl>
              <FormLabel fontSize="sm" color="text-primary">
                Source Task
              </FormLabel>
              <Select
                bg="bg-surface"
                borderColor="border-default"
                size="sm"
                value={watchedApprovalConfig.valueSourceTaskId || ''}
                onChange={(e) => setValue('approvalConfig.valueSourceTaskId', e.target.value || undefined)}
                placeholder="Select the task whose value to include"
              >
                {availableTasks.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </Select>
              <Text fontSize="xs" color="text-muted" mt={1}>
                The submitted value of this task will be the third body parameter for any level whose
                resolver needs it. Pick this task itself to send the value the maker has just entered.
              </Text>
            </FormControl>
          )}

          {/* Session message texts */}
          {!isAlertMode && (
            <Box>
              <FormLabel fontSize="sm" color="text-primary" mb={1}>
                Notification Messages
              </FormLabel>
              <Text fontSize="xs" color="text-muted" mb={3}>
                Plain session messages sent outside the approval template. Leave any field blank
                to keep the default wording. Available placeholders:{' '}
                <Text as="span" fontFamily="mono">
                  {'{{task}}'} {'{{description}}'} {'{{maker}}'} {'{{approver}}'} {'{{value}}'}
                </Text>
              </Text>
              <VStack spacing={3} align="stretch">
                {MESSAGE_FIELDS.map((field) => (
                  <FormControl key={field.key}>
                    <FormLabel fontSize="sm" color="text-secondary" mb={1}>
                      {field.label}
                    </FormLabel>
                    <Textarea
                      bg="bg-surface"
                      borderColor="border-default"
                      size="sm"
                      rows={2}
                      placeholder={field.placeholder}
                      value={watchedApprovalConfig[field.key] || ''}
                      onChange={(e) => setValue(`approvalConfig.${field.key}`, e.target.value)}
                    />
                    <Text fontSize="xs" color="text-muted" mt={1}>
                      {field.helper}
                    </Text>
                  </FormControl>
                ))}
              </VStack>
            </Box>
          )}

          {/* Document Parser — only for doc_required + MAKERCHECKER */}
          {!isAlertMode && isDocumentWithMakerChecker && (
            <FormControl>
              <Checkbox
                isChecked={watchedApprovalConfig.parserEnabled ?? false}
                onChange={(e) => setValue('approvalConfig.parserEnabled', e.target.checked)}
                colorScheme="brand"
                borderColor="border-muted"
                sx={{ '[data-checked]': { borderColor: 'brand.500', backgroundColor: 'brand.500' } }}
              >
                Enable Document Parser
              </Checkbox>
              {watchedApprovalConfig.parserEnabled && (
                <Box mt={2}>
                  <FormLabel fontSize="sm" color="text-secondary">
                    Parser Type
                  </FormLabel>
                  <Input
                    placeholder="Enter parser identifier (e.g. invoice_parser)"
                    bg="bg-surface"
                    size="sm"
                    value={watchedApprovalConfig.parserType || ''}
                    onChange={(e) => setValue('approvalConfig.parserType', e.target.value)}
                  />
                </Box>
              )}
            </FormControl>
          )}
        </VStack>
      </Box>
    </>
  );
};
